import React, { useState } from 'react';

interface LanguageSelectorProps {
    onLanguageSelected: (language: 'fr' | 'en') => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onLanguageSelected }) => {
    const [selectedLang, setSelectedLang] = useState<'fr' | 'en' | null>(null);

    const handleSelect = (lang: 'fr' | 'en') => {
        setSelectedLang(lang);
        
        // Animation de sélection puis transition
        setTimeout(() => {
            onLanguageSelected(lang);
        }, 300);
    };

    return (
        <div className="fixed inset-0 bg-black flex items-center justify-center font-mono">
            {/* Scanlines CRT */}
            <div className="absolute inset-0 pointer-events-none opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.03) 2px, rgba(0, 255, 255, 0.03) 4px)',
                }}></div>
            </div>

            {/* Contenu principal */}
            <div className="relative z-10 w-full max-w-2xl px-8 animate-in fade-in duration-500">
                {/* En-tête système */}
                <div className="border-2 border-cyan-600 bg-slate-950/90 p-8 shadow-2xl shadow-cyan-900/50">
                    {/* Barre de titre */}
                    <div className="border-b-2 border-cyan-700 pb-4 mb-6">
                        <div className="text-cyan-400 text-xs tracking-widest mb-2">
                            ALBION OS v2.0 - SYSTEM CONFIGURATION
                        </div>
                        <h1 className="text-2xl font-bold text-cyan-400 tracking-wider">
                            INTERFACE LANGUAGE / LANGUE D'INTERFACE
                        </h1>
                    </div>

                    {/* Instructions */}
                    <div className="mb-8 space-y-3">
                        <p className="text-slate-400 text-sm">
                            &gt; Select your preferred system language
                        </p>
                        <p className="text-slate-400 text-sm">
                            &gt; Sélectionnez votre langue système préférée
                        </p>
                        
                        {/* Avertissement important */}
                        <div className="mt-4 p-3 border border-yellow-600/50 bg-yellow-950/20">
                            <div className="flex items-start gap-2">
                                <div className="text-yellow-500 text-lg mt-0.5">⚠</div>
                                <div className="flex-1 space-y-2">
                                    <p className="text-yellow-400 text-xs font-bold tracking-wide">
                                        IMPORTANT / IMPORTANT
                                    </p>
                                    <p className="text-yellow-200/90 text-xs leading-relaxed">
                                        <span className="font-semibold">English:</span> The entire game will be in the language you choose now. If you change language later in settings, only menus and buttons will switch - documents and messages already received will stay in the original language.
                                    </p>
                                    <p className="text-yellow-200/90 text-xs leading-relaxed">
                                        <span className="font-semibold">Français :</span> Tout le jeu sera dans la langue que vous choisissez maintenant. Si vous changez de langue plus tard dans les paramètres, seuls les menus et boutons changeront - les documents et messages déjà reçus resteront dans la langue d'origine.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Options de langue */}
                    <div className="space-y-4 mb-8">
                        {/* Option Français */}
                        <button
                            onClick={() => handleSelect('fr')}
                            className={`
                                w-full p-4 border-2 transition-all duration-200 text-left
                                ${selectedLang === 'fr' 
                                    ? 'border-cyan-400 bg-cyan-950/50 shadow-lg shadow-cyan-500/50' 
                                    : 'border-slate-600 bg-slate-900/50 hover:border-cyan-600 hover:bg-slate-800/70'
                                }
                            `}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-3 h-3 border-2 border-cyan-400 flex items-center justify-center">
                                        {selectedLang === 'fr' && (
                                            <div className="w-1.5 h-1.5 bg-cyan-400"></div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-cyan-400 font-bold text-lg tracking-wider">
                                            FRANÇAIS
                                        </div>
                                        <div className="text-slate-500 text-xs mt-1">
                                            French - France
                                        </div>
                                    </div>
                                </div>
                                <div className="text-slate-600 text-xs">
                                    [FR]
                                </div>
                            </div>
                        </button>

                        {/* Option English */}
                        <button
                            onClick={() => handleSelect('en')}
                            className={`
                                w-full p-4 border-2 transition-all duration-200 text-left
                                ${selectedLang === 'en' 
                                    ? 'border-cyan-400 bg-cyan-950/50 shadow-lg shadow-cyan-500/50' 
                                    : 'border-slate-600 bg-slate-900/50 hover:border-cyan-600 hover:bg-slate-800/70'
                                }
                            `}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-3 h-3 border-2 border-cyan-400 flex items-center justify-center">
                                        {selectedLang === 'en' && (
                                            <div className="w-1.5 h-1.5 bg-cyan-400"></div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-cyan-400 font-bold text-lg tracking-wider">
                                            ENGLISH
                                        </div>
                                        <div className="text-slate-500 text-xs mt-1">
                                            English - United States
                                        </div>
                                    </div>
                                </div>
                                <div className="text-slate-600 text-xs">
                                    [EN]
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="border-t-2 border-slate-700 pt-4">
                        <div className="text-slate-600 text-xs">
                            &gt; This setting will apply to all system interfaces
                        </div>
                        <div className="text-slate-600 text-xs">
                            &gt; Ce paramètre s'appliquera à toutes les interfaces système
                        </div>
                    </div>
                </div>

                {/* Indicateur de chargement si sélection en cours */}
                {selectedLang && (
                    <div className="mt-4 text-center">
                        <div className="text-cyan-400 text-sm animate-pulse">
                            &gt; LOADING LANGUAGE CONFIGURATION...
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
